import { useEffect, useRef, useCallback, useState } from "react";
import {
  MAPS_API_KEY,
  loadGoogleMaps,
  getMapStyleOptions,
  createMarker,
  onMarkerClick,
  removeMarker,
  openInfoWindowAt,
  USE_ADVANCED_MARKERS,
  type AnyMarker,
} from "../../lib/google-maps";
import { MarkerClusterer, SuperClusterAlgorithm, type Renderer, type Cluster, type ClusterStats } from "@googlemaps/markerclusterer";

// Station shape matching the API
interface MapStation {
  code: string;
  name: string;
  brand: string;
  address: string;
  location: { lat: number; lng: number };
  prices: Record<string, { price: number; updated: string }>;
}

interface StationMapProps {
  stations: MapStation[];
  selectedFuel: string;
  center?: { lat: number; lng: number };
  zoom?: number;
  userLocation?: { lat: number; lng: number } | null;
  onStationClick?: (station: MapStation) => void;
  highlightedStation?: string | null;
}

const SYDNEY = { lat: -33.8688, lng: 151.2093 };

function getBrandAbbr(brand: string): string {
  const map: Record<string, string> = {
    "7-Eleven": "7", "Ampol": "A", "BP": "BP", "Caltex": "C",
    "Coles Express": "CE", "Costco": "Co", "EG": "EG",
    "Independent": "IN", "Liberty": "LB", "Lowes": "Lo",
    "Metro Fuel": "MF", "Mobil": "M", "Pearl Energy": "PE",
    "Puma": "PU", "Shell": "SH", "Speedway": "SP",
    "United": "U", "Vibe": "V", "Woolworths": "WW",
    "Budget": "BU", "Caltex Woolworths": "CW", "Prime": "PR",
  };
  return map[brand] || brand.slice(0, 2).toUpperCase();
}

function pricePillSvg(color: string, priceText: string, brand?: string): string {
  if (!brand) {
    const width = 52;
    const height = 24;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect x="1" y="1" width="${width - 2}" height="${height - 2}" rx="12" ry="12" fill="${color}" stroke="#000" stroke-width="1"/>
      <text x="${width / 2}" y="${height / 2 + 1}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-family="system-ui,sans-serif" font-size="11" font-weight="700">${priceText}</text>
    </svg>`;
  }
  const abbr = getBrandAbbr(brand);
  const width = 72;
  const height = 30;
  const badgeW = 22;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <rect x="1" y="1" width="${width - 2}" height="${height - 2}" rx="14" ry="14" fill="#1e1e2e" stroke="${color}" stroke-width="1.5" opacity="0.95"/>
    <rect x="3" y="4" width="${badgeW}" height="${height - 8}" rx="10" ry="10" fill="${color}" opacity="0.25"/>
    <text x="${3 + badgeW / 2}" y="${height / 2 + 1}" text-anchor="middle" dominant-baseline="central" fill="${color}" font-family="system-ui,sans-serif" font-size="9" font-weight="800">${abbr}</text>
    <text x="${badgeW + 8 + (width - badgeW - 10) / 2}" y="${height / 2 + 1}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-family="system-ui,sans-serif" font-size="12" font-weight="700">${priceText}</text>
  </svg>`;
}

function getPriceColor(price: number, allPrices: number[]): string {
  if (allPrices.length === 0) return "#ca8a04";
  const sorted = [...allPrices].sort((a, b) => a - b);
  const q25 = sorted[Math.floor(sorted.length * 0.25)];
  const q75 = sorted[Math.floor(sorted.length * 0.75)];
  if (price <= q25) return "#16a34a";
  if (price >= q75) return "#dc2626";
  return "#ca8a04";
}

/** Compact dark-themed cluster renderer */
class DarkClusterRenderer implements Renderer {
  render({ count, position }: Cluster, _stats: ClusterStats, _map: google.maps.Map) {
    // Compact sizing: 28–44px based on count
    const size = Math.min(44, 28 + Math.floor(Math.log10(Math.max(count, 1)) * 10));
    const half = size / 2;
    // Subtle accent color
    const accent = count >= 100 ? "#f97316" : count >= 30 ? "#eab308" : "#6b7280";

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${half}" cy="${half}" r="${half - 1}" fill="#1e1e2e" opacity="0.88"/>
      <circle cx="${half}" cy="${half}" r="${half - 1}" fill="${accent}" opacity="0.15"/>
      <circle cx="${half}" cy="${half}" r="${half - 1}" stroke="${accent}" stroke-width="1.5" fill="none" opacity="0.6"/>
      <text x="${half}" y="${half + 1}" text-anchor="middle" dominant-baseline="central" fill="#e2e2e2" font-family="system-ui,sans-serif" font-size="${size <= 30 ? 11 : 12}" font-weight="600">${count}</text>
    </svg>`;

    if (USE_ADVANCED_MARKERS) {
      const div = document.createElement("div");
      div.innerHTML = svg;
      div.style.width = `${size}px`;
      div.style.height = `${size}px`;
      div.style.cursor = "pointer";
      return new google.maps.marker.AdvancedMarkerElement({
        position,
        content: div,
        zIndex: 1000 + count,
      }) as unknown as google.maps.Marker;
    }

    const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    return new google.maps.Marker({
      position,
      icon: {
        url: dataUrl,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(half, half),
      },
      zIndex: 1000 + count,
    });
  }
}

export function StationMap({
  stations,
  selectedFuel,
  center,
  zoom = 11,
  userLocation,
  onStationClick,
  highlightedStation,
}: StationMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<AnyMarker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const userMarkerRef = useRef<AnyMarker | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the Maps JS API
  useEffect(() => {
    if (!MAPS_API_KEY) {
      setError("Google Maps API key is not configured.");
      return;
    }
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch(() => setError("Failed to load Google Maps. Please try again later."));
  }, []);

  // Initialize map once loaded
  useEffect(() => {
    if (!loaded || !mapRef.current || mapInstance.current) return;

    mapInstance.current = new google.maps.Map(mapRef.current, {
      center: center ?? SYDNEY,
      zoom,
      ...getMapStyleOptions(),
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM },
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      backgroundColor: "#1a1a2e",
    });

    infoWindowRef.current = new google.maps.InfoWindow();
  }, [loaded, center, zoom]);

  // Open info window for a given station
  const openInfoWindow = useCallback(
    (station: MapStation, marker: AnyMarker) => {
      if (!infoWindowRef.current || !mapInstance.current) return;
      const priceInfo = station.prices[selectedFuel];
      const priceStr = priceInfo ? `${priceInfo.price.toFixed(1)} ¢/L` : "N/A";

      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${station.location.lat},${station.location.lng}`;
      infoWindowRef.current.setContent(
        `<div style="color:#e2e2e2;padding:10px 12px;padding-right:28px;font-family:system-ui,sans-serif;min-width:150px;position:relative">
          <button onclick="this.closest('.gm-style-iw-a')?.querySelector('button[aria-label]')?.click()" style="position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:50%;border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.08);color:#9ca3af;font-size:14px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0" aria-label="Close">✕</button>
          <div style="font-weight:700;font-size:13px;margin-bottom:2px">${station.name}</div>
          <div style="font-size:11px;color:#9ca3af;margin-bottom:4px">${station.brand}</div>
          <div style="font-size:20px;font-weight:800;font-family:monospace;color:#fff;margin-bottom:4px">${priceStr}</div>
          <div style="font-size:10px;color:#6b7280;margin-bottom:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px">${station.address}</div>
          <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:4px;padding:5px 10px;background:rgba(249,115,22,0.2);border:1px solid rgba(249,115,22,0.4);border-radius:6px;color:#f97316;font-size:11px;font-weight:600;text-decoration:none">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8c0 4.5-6 11-6 11s-6-6.5-6-11a6 6 0 0 1 12 0z"/><circle cx="12" cy="8" r="2"/></svg>
            Open in Maps
          </a>
        </div>`
      );
      openInfoWindowAt(infoWindowRef.current, mapInstance.current, marker);
    },
    [selectedFuel]
  );

  // Sync markers whenever stations / fuel changes
  useEffect(() => {
    if (!mapInstance.current || !loaded) return;

    // Clear existing clusterer and markers
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }
    markersRef.current.forEach((m) => removeMarker(m));
    markersRef.current = [];

    const allPrices = stations
      .map((s) => s.prices[selectedFuel]?.price)
      .filter((p): p is number => p != null);

    const newMarkers: AnyMarker[] = [];

    // Create a marker for every station (clustering is independent of fuel selection)
    stations.forEach((station) => {
      const priceInfo = station.prices[selectedFuel];
      const price = priceInfo?.price;
      const color = price != null ? getPriceColor(price, allPrices) : "#6b7280";
      const priceText = price != null ? price.toFixed(1) : "—";

      // Small dot for clustered view; branded pill shown when zoomed in past cluster maxZoom
      const svgHtml = pricePillSvg(color, priceText, station.brand);

      const marker = createMarker({
        position: station.location,
        svgHtml,
        width: 72,
        height: 30,
        title: price != null ? `${station.name} - ${priceText}¢` : station.name,
      });

      onMarkerClick(marker, () => {
        openInfoWindow(station, marker);
        onStationClick?.(station);
      });

      (marker as AnyMarker & { _stationCode: string })._stationCode = station.code;
      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;

    // Always use clustering — large radius merges aggressively at low zoom,
    // dissolves naturally at maxZoom so branded pills appear
    clustererRef.current = new MarkerClusterer({
      map: mapInstance.current,
      markers: newMarkers as google.maps.Marker[],
      renderer: new DarkClusterRenderer(),
      algorithm: new SuperClusterAlgorithm({ radius: 120, maxZoom: 14 }),
    });
  }, [stations, selectedFuel, loaded, openInfoWindow, onStationClick]);

  // Handle highlighted station from list click
  useEffect(() => {
    if (!highlightedStation || !mapInstance.current) return;

    const marker = markersRef.current.find(
      (m) => (m as AnyMarker & { _stationCode: string })._stationCode === highlightedStation
    );
    if (!marker) return;

    const station = stations.find((s) => s.code === highlightedStation);
    if (!station) return;

    mapInstance.current.panTo(station.location);
    mapInstance.current.setZoom(14);
    openInfoWindow(station, marker);
  }, [highlightedStation, stations, openInfoWindow]);

  // User location marker
  useEffect(() => {
    if (!mapInstance.current || !loaded) return;

    if (userMarkerRef.current) {
      removeMarker(userMarkerRef.current);
      userMarkerRef.current = null;
    }

    if (userLocation) {
      const svgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
        <circle cx="11" cy="11" r="8" fill="#3b82f6" stroke="#fff" stroke-width="3"/>
      </svg>`;
      userMarkerRef.current = createMarker({
        position: userLocation,
        map: mapInstance.current!,
        svgHtml,
        width: 22,
        height: 22,
        title: "Your location",
        zIndex: 999,
      });
    }
  }, [userLocation, loaded]);

  // Pan to center when center prop changes
  useEffect(() => {
    if (center && mapInstance.current) {
      mapInstance.current.panTo(center);
    }
  }, [center]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-card border border-white/5 rounded-2xl">
        <div className="text-center p-8">
          <div className="text-4xl mb-4">🗺️</div>
          <p className="text-sm text-muted-foreground max-w-xs">{error}</p>
        </div>
      </div>
    );
  }

  if (!MAPS_API_KEY) {
    return (
      <div className="flex items-center justify-center h-full bg-card border border-white/5 rounded-2xl">
        <div className="text-center p-8">
          <div className="text-4xl mb-4">🔑</div>
          <h3 className="font-bold mb-2">Map Unavailable</h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            Google Maps API key is not configured. Set <code className="text-xs bg-white/10 px-1 py-0.5 rounded">VITE_MAPS_API_KEY</code> to enable the map.
          </p>
        </div>
      </div>
    );
  }

  return <div ref={mapRef} className="w-full h-full" />;
}

export type { MapStation };
